<template>
  <div class="p-newsletter__right-col relative">
    <form autocomplete="off" @submit.prevent="onSubmitForm">
      <Loader v-if="isLoading"></Loader>
      <div class="b-block b-block--transparent b-block--sm-pad">
        <Alert v-if="errorMsgs.length" type="danger" :messages="errorMsgs" />
        <div class="b-block__form-section">
          <div class="b-block__mini-title">
            {{ dictionary.personalInformation }}
          </div>
          <!-- firstName -->
          <div class="form-floating">
            <input
              id="firstName"
              v-model.trim="firstName"
              type="text"
              class="form-control"
              :placeholder="dictionary.firstNamePlaceholder"
              :class="{ 'form-control--error': v$.firstName.$error }"
              @blur="v$.firstName.$touch" />
            <label for="firstName">{{ dictionary.firstNameLabel }}</label>

            <template v-if="v$.firstName.$error">
              <div
                v-if="v$.firstName.required.$invalid"
                class="field-validation-error">
                {{ dictionary.firstNameRequired }}
              </div>
              <div
                v-if="
                  v$.firstName.minLength.$invalid ||
                  v$.firstName.maxLength.$invalid
                "
                class="field-validation-error">
                {{ dictionary.firstNameLength }}
              </div>
              <div
                v-if="v$.firstName.regex.$invalid"
                class="field-validation-error">
                {{ dictionary.firstNameAlpha }}
              </div>
            </template>
          </div>
          <!-- lastName -->
          <div class="form-floating">
            <input
              id="lastName"
              v-model.trim="lastName"
              type="text"
              class="form-control"
              :placeholder="dictionary.lastNamePlaceholder"
              :class="{ 'form-control--error': v$.lastName.$error }"
              @blur="v$.lastName.$touch" />
            <label for="lastName">{{ dictionary.lastNameLabel }}</label>

            <template v-if="v$.lastName.$error">
              <div
                v-if="v$.lastName.required.$invalid"
                class="field-validation-error">
                {{ dictionary.lastNameRequired }}
              </div>
              <div
                v-if="
                  v$.lastName.minLength.$invalid ||
                  v$.lastName.maxLength.$invalid
                "
                class="field-validation-error">
                {{ dictionary.lastNameLength }}
              </div>
              <div
                v-if="v$.lastName.regex.$invalid"
                class="field-validation-error">
                {{ dictionary.lastNameAlpha }}
              </div>
            </template>
          </div>
          <!-- email -->
          <div class="form-floating">
            <input
              id="email"
              v-model="email"
              type="email"
              class="form-control"
              :placeholder="dictionary.emailLabel"
              :class="{ 'form-control--error': v$.email.$error }"
              @blur="v$.email.$touch" />
            <label for="email">{{ dictionary.emailLabel }}</label>
            <template v-if="v$.email.$error">
              <div
                v-if="v$.email.required.$invalid"
                class="field-validation-error">
                {{ dictionary.emailRequired }}
              </div>
              <div
                v-if="v$.email.email.$invalid"
                class="field-validation-error">
                {{ dictionary.emailRegex }}
              </div>
            </template>
          </div>
          <!-- terms -->
          <div class="custom-chck custom-chck--additional-style">
            <input
              id="user-terms"
              v-model="terms"
              type="checkbox"
              :class="{ 'form-control--error': v$.terms.$error }"
              @change="v$.terms.$touch" />
            <label for="user-terms">
              <span class="custom-chck__span">
                <div
                  class="custom-chck__text"
                  v-html="dictionary.termsDesc"></div>
              </span>
            </label>
          </div>
          <template v-if="v$.terms.$error">
            <div
              v-if="v$.terms.checked.$invalid"
              class="field-validation-error">
              {{ dictionary.termsRequired }}
            </div>
          </template>
        </div>
        <!-- news letter options -->
        <div class="b-block__form-section">
          <div class="b-block__mini-title">
            {{ dictionary.newsLetterOptionsTitle }}
          </div>

          <div
            v-for="(item, index) in newsletterOptions"
            :key="index"
            class="custom-chck">
            <input
              :id="item.id"
              v-model="selectedNewsletters"
              type="checkbox"
              :value="item.value"
              :disabled="item.disabled"
              :name="item.id" />
            <label class="custom-chck__span" :for="item.id">
              {{ item.text }}
            </label>
          </div>
        </div>

        <div class="p-contact__form-message">
          {{ dictionary.requiredInputs }}
        </div>
      </div>
      <div class="p-contact__submit-area">
        <div class="b-block__form-btn">
          <button
            type="submit"
            class="p-contact__submit-btn"
            :disabled="submitButtonIsDisabled">
            <span>{{ dictionary.buttonSubmitText }}</span>
          </button>
        </div>
      </div>
    </form>
    <ModalOverlay
      v-model="showSuccessModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ dictionary.submitSuccessHeader }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div
            class="b-pop-up__alert-success__icon b-pop-up__alert-success__icon--success"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ finalSuccessTitle }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ finalSuccessText }}
            </div>
            <button
              type="button"
              class="b-pop-up__alert-success__cta"
              @click="() => toggleSuccessModal(false)">
              {{ dictionary.submitSuccessClose }}
            </button>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
import {
  checkCaptcha,
  getData,
  scrollToError,
  scrollToValidation,
} from './../../lib/lib'
import constants from '../../lib/constants'
import Loader from './../components/Shared/Loader.vue'
import Alert from './../components/Shared/Alert.vue'
import ModalOverlay from './../components/ModalOverlay.vue'
import useVuelidate from '@vuelidate/core'
import {
  email,
  helpers,
  maxLength,
  minLength,
  required,
} from '@vuelidate/validators'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'

const { greekLatin } = constants.regex

export default {
  components: { Loader, Alert, ModalOverlay },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      dictionary: {},
      showSuccessModal: false,
      newsletterOptions: [],
      // form properties
      email: '',
      firstName: '',
      lastName: '',
      terms: false,
      selectedNewsletters: [],

      finalSuccessTitle: '',
      finalSuccessText: '',
    }
  },
  validations() {
    return {
      email: {
        required,
        email,
      },
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatin),
      },
      lastName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatin),
      },
      terms: {
        checked: (value) => value === true,
      },
    }
  },
  computed: {
    submitButtonIsDisabled() {
      return this.isLoading || this.v$.$invalid
    },
  },
  mounted() {
    checkCaptcha(() => (this.isLoading = false))

    this.setFormData()
  },
  methods: {
    setFormData() {
      const { newsLetterOptions, dictionary } = getData('newsLetterFormData')
      this.dictionary = dictionary
      this.newsletterOptions = newsLetterOptions
      this.defaultSelectedNewsletters()
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          scrollToValidation()
        })
        return false
      }

      this.isLoading = true
      this.errorMsgs = []

      const postData = {
        email: this.email,
        firstName: this.firstName,
        lastName: this.lastName,
        newsletterOptions: this.selectedNewsletters,
        terms: this.terms,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaAction,
      })
    },

    async handleSuccess(data) {
      this.isLoading = false
      this.errorMsgs = []
      const title = data?.data?.popupMessageTitle
        ? data.data.popupMessageTitle
        : this.dictionary.submitSuccessTitle
      const text = data?.data?.popupMessageText
        ? data.data.popupMessageText
        : this.dictionary.submitSuccessContent
      this.finalSuccessTitle = title
      this.finalSuccessText = text
      await this.$nextTick()
      this.toggleSuccessModal(true)
      this.resetFormData()
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      scrollToError()
    },

    toggleSuccessModal(show) {
      this.showSuccessModal = show
    },
    resetFormData() {
      this.v$.$reset()
      this.email = ''
      this.firstName = ''
      this.lastName = ''
      this.terms = false
      // this.selectedNewsletters = []
      this.defaultSelectedNewsletters()
    },
    async defaultSelectedNewsletters() {
      await this.$nextTick()

      this.selectedNewsletters = this.newsletterOptions
        .filter((item) => item.selected)
        .map((item) => item.value)
    },
  },
}
</script>
